import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ColumnInstance,
  HeaderGroup,
  TableInstance,
  useFilters,
  useGlobalFilter,
  useSortBy,
  UseSortByColumnProps,
  useTable,
} from 'react-table';
import './AlarmHistoryTable.scss';
import { Column } from 'react-table';
import { DefaultColumnFilter } from './helpers/DefaultColumnFilter';
import { FixedSizeList as FixedSizeList } from 'react-window';
import { AlarmHistoryItem } from '../well-alarms/AlarmHistoryModal';
import { createMultiConditionFilter } from '../../utilities/tableFilters';
import { formatDate } from '../../utilities/dateHelpers';

interface AlarmHistoryTableProps {
  alarmHistoryItems: AlarmHistoryItem[];
  onDataReady?: (rows: any[]) => void;
}

const AlarmHistoryTable: React.FC<AlarmHistoryTableProps> = ({ alarmHistoryItems, onDataReady }) => {
  // Use a stable reference for data to prevent unnecessary re-renders
  const [stableData, setStableData] = useState(alarmHistoryItems);
  const [currentFilters, setCurrentFilters] = useState<any[]>([]);
  const currentFiltersRef = useRef(currentFilters);
  const listRef = useRef<FixedSizeList>(null);

  // Update stable data when alarmHistoryItems prop changes
  useEffect(() => {
    setStableData(alarmHistoryItems);
  }, [alarmHistoryItems]);

  const data = useMemo(() => stableData, [stableData]);

  useEffect(() => {
    currentFiltersRef.current = currentFilters; // Keep the ref updated with the latest value of currentFilters
  }, [currentFilters]);

  const multiConditionFilter = createMultiConditionFilter(setCurrentFilters, currentFiltersRef);

  const columns: Column<AlarmHistoryItem>[] = useMemo(
    () => [
      {
        Header: 'Date',
        accessor: 'alarmDate' as const,
        filter: multiConditionFilter,
        Cell: ({ value }: { value: string }) => <span>{formatDate(value, true)}</span>,
        sortType: (rowA: any, rowB: any, columnId: string) => {
          const dateA = new Date(rowA.values[columnId]);
          const dateB = new Date(rowB.values[columnId]);

          if (dateA < dateB) return -1;
          if (dateA > dateB) return 1;
          return 0;
        }
      },
      { Header: 'Source', accessor: 'alarmSource', filter: multiConditionFilter },
      {
        Header: 'Description',
        accessor: 'alarmDescription',
        filter: multiConditionFilter,
        Cell: ({ value }: { value: string }) => {
          const description = value || '';
          const lowerDescription = description.toLowerCase();
          const isBlue = lowerDescription.includes('clear') || lowerDescription.includes('309');

          return (
            <span className={`${isBlue ? 'alarm-description-blue' : 'alarm-description-red'}`}>{description}</span>
          );
        },
      },
    ],
    [],
  );

  const defaultColumn: any = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: (props: any) => (
        <DefaultColumnFilter
          {...props}
          clearFilterForColumn={clearFilterForColumn}
          currentFiltersRef={currentFiltersRef}
        />
      ),
    }),
    [],
  );

  const tableConfig = useMemo(
    () => ({
      columns,
      data,
      defaultColumn: defaultColumn,
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetRowState: false,
      getRowId: (_row: AlarmHistoryItem, index: number) => `alarm-${index}`, // Stable row IDs
    }),
    [columns, data, defaultColumn],
  );

  const instance = useTable<AlarmHistoryItem>(tableConfig, useGlobalFilter, useFilters, useSortBy);

  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
    setAllFilters,
  } = instance as TableInstance<AlarmHistoryItem> & {
    setAllFilters: (filters: any[]) => void;
  };

  // Call onDataReady when rows are available
  useEffect(() => {
    if (onDataReady && rows.length > 0) {
      onDataReady(rows);
    }
  }, [rows, onDataReady]);

  const clearFilterForColumn = (columnId: string) => {
    const remainingFilters = currentFiltersRef.current.filter((filter) => filter.columnId !== columnId);
    setCurrentFilters(remainingFilters);

    if (remainingFilters.length === 0) {
      setAllFilters([]);
    } else {
      setAllFilters(
        remainingFilters.map((filter) => ({
          id: filter.columnId,
          value: filter.filterValue,
        })),
      );
    }
  };

  const columnWidths: Record<string, number | string> = {
    alarmDate: 300,
    alarmSource: 300,
    alarmDescription: 'auto',
  };

  return (
    <section className='alarm-table-container-wrapper'>
      <div className='table-container alarm-table-container'>
        <div className='table-header-wrapper'>
          <table {...getTableProps()} className='data-table alarm-detail-table'>
            <thead>
              {headerGroups.map((headerGroup: HeaderGroup<any>) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((col) => {
                    const column = col as unknown as ColumnInstance<any> & UseSortByColumnProps<any> as any;
                    return (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        className={`${column.id}-column`}
                        style={{
                          width: columnWidths[column.id],
                          flex: column.id === 'alarmDescription' ? '1' : 'none',
                        }}
                      >
                        <div className='flex items-center alarm-detail'>
                          <span className='text-[12px] font-semibold text-[#f7f9f9]'>{column.render('Header')}</span>
                          {column.isSorted && (
                            <span
                              className={`tdt-sort-icon ${column.isSortedDesc ? 'desc' : 'asc'}`}
                              style={{ display: column.render('Header') ? 'inline-block' : 'none' }}
                            />
                          )}
                          <span>{column.canFilter ? column.render('Filter') : null}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
          </table>
        </div>
        <div>
          {data.length > 0 ? (
            <div className='virtualized-table-body'>
              {rows.length === 0 ? (
                <div className='text-center p-20 h-[400px] flex items-center justify-center'>
                  <h1 className='text-gray-400'>
                    No data found for the selected filter conditions. Please adjust your filters and try again.
                  </h1>
                </div>
              ) : (
                <FixedSizeList
                  key='alarm-history-list'
                  ref={listRef}
                  height={420}
                  itemCount={rows.length}
                  itemSize={45}
                  width='100%'
                >
                  {({ index, style }) => {
                    const row = rows[index];
                    prepareRow(row);
                    return (
                      <div {...row.getRowProps({ style })} className='alarm-row'>
                        {row.cells.map((cell) => (
                          <div
                            {...cell.getCellProps()}
                            className={`aht-alarm-cell ${cell.column.id}-column`}
                            style={{
                              width: columnWidths[cell.column.id],
                              flex: cell.column.id === 'alarmDescription' ? '1' : 'none',
                            }}
                          >
                            {cell.render('Cell')}
                          </div>
                        ))}
                      </div>
                    );
                  }}
                </FixedSizeList>
              )}
            </div>
          ) : (
            <div className='text-center p-20 h-[400px] flex items-center justify-center'>
              <h1 className='text-gray-400'>No Alarm data available.</h1>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AlarmHistoryTable;
